import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "pk_install_dismissed_at";
const HIDE_FOR_DAYS = 14;

/** Add-to-home-screen prompt. Chrome/Android uses beforeinstallprompt; iOS gets manual steps. */
export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (standalone) return;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < HIDE_FOR_DAYS * 864e5) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    if (isIos) {
      const t = setTimeout(() => setShowIosHint(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDeferred(null);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  if (!deferred && !showIosHint) return null;

  return (
    <div
      className="md:hidden fixed inset-x-3 z-[60] border border-[#00FF66]/40 bg-[#0f0f0f] shadow-2xl rounded-sm"
      style={{ bottom: "calc(5rem + env(safe-area-inset-bottom))" }}
      data-testid="install-prompt"
    >
      <div className="flex items-start gap-3 p-3">
        <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-sm flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg leading-tight">PUT IT ON YOUR HOME SCREEN</div>
          <p className="text-white/50 text-xs mt-0.5">
            {deferred
              ? "Full screen, one tap to your picks."
              : "Tap Share, then \u201cAdd to Home Screen\u201d."}
          </p>
          {deferred && (
            <button
              onClick={install}
              data-testid="install-accept-btn"
              className="mt-2 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs px-3 py-2 rounded-sm inline-flex items-center gap-1.5"
            >
              <Download size={13} /> Install
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          data-testid="install-dismiss-btn"
          className="p-1 text-white/40 active:text-white flex-shrink-0"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
